class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.addEventListener(
      'click',
      (event) => {
        const tbiButton = event.target.closest('#cartDrawerTbiButton');
        if (!tbiButton) return;

        event.preventDefault();
        event.stopPropagation();
        this.handleTbiClick(tbiButton);
      },
      { capture: true }
    );
    this.setHeaderCartIconAccessibility();
    this.prepareRecommendation();
  }

  getTopSellingProducts() {
    if (!window.tsTopSellingProductsPromise) {
      const root = window.Shopify?.routes?.root || '/';
      const normalizedRoot = root.endsWith('/') ? root : `${root}/`;
      const params = new URLSearchParams({ sort_by: 'best-selling', view: 'top50' });
      const theme = window.Shopify?.theme;

      if (theme?.id && theme.role && theme.role !== 'main') {
        params.set('preview_theme_id', theme.id);
      }

      const url = `${normalizedRoot}collections/all?${params.toString()}`;

      window.tsTopSellingProductsPromise = fetch(url, {
        method: 'GET',
        credentials: 'same-origin',
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Top sellers request failed with status ${response.status}: ${response.url}`);
          }
          return response.json();
        })
        .then((payload) => {
          if (!Array.isArray(payload.products) || !payload.products.length) {
            throw new Error('Top sellers response did not contain products');
          }

          return payload.products.slice(0, 50);
        })
        .catch((error) => {
          window.tsTopSellingProductsPromise = null;
          throw error;
        });
    }

    return window.tsTopSellingProductsPromise;
  }

  getRandomIndex(length) {
    if (window.crypto?.getRandomValues) {
      const value = new Uint32Array(1);
      window.crypto.getRandomValues(value);
      return Math.floor((value[0] / 4294967296) * length);
    }

    return Math.floor(Math.random() * length);
  }

  preloadRecommendationImage(src) {
    return new Promise((resolve, reject) => {
      const image = new Image();

      image.onload = async () => {
        if (typeof image.decode === 'function') {
          await image.decode().catch(() => undefined);
        }

        resolve();
      };
      image.onerror = () => reject(new Error('Recommendation image could not be loaded'));
      image.src = src;
    });
  }

  renderCartRecommendation(product) {
    const recommendation = this.querySelector('[data-ts-cart-recommendation]');
    if (!recommendation) return;

    const productUrl = new URL(product.url, window.location.origin);
    if (productUrl.origin !== window.location.origin) return;

    recommendation.dataset.productId = String(product.id);
    recommendation.dataset.bestSellerRank = String(product.rank || '');

    recommendation
      .querySelectorAll('[data-ts-recommendation-media], [data-ts-recommendation-title], [data-ts-recommendation-link]')
      .forEach((link) => link.setAttribute('href', productUrl.href));

    const media = recommendation.querySelector('[data-ts-recommendation-media]');
    let image = media?.querySelector('img');

    if (!image && media && product.image) {
      image = document.createElement('img');
      image.loading = 'lazy';
      image.width = 480;
      image.height = 480;
      media.appendChild(image);
    }

    if (image && product.image) {
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.src = product.image;
      image.alt = product.title;
    }

    const vendor = recommendation.querySelector('[data-ts-recommendation-vendor]');
    const title = recommendation.querySelector('[data-ts-recommendation-title]');
    const price = recommendation.querySelector('[data-ts-recommendation-price]');

    if (vendor) vendor.textContent = product.vendor || '';
    if (title) title.textContent = product.title;

    if (price) {
      price.replaceChildren();

      if (Number(product.compare_at_price) > Number(product.price)) {
        const comparePrice = document.createElement('s');
        comparePrice.textContent = product.compare_at_price_formatted;
        price.appendChild(comparePrice);
      }

      const currentPrice = document.createElement('strong');
      currentPrice.textContent = product.price_formatted;
      price.appendChild(currentPrice);
    }

    this.lastRecommendationId = Number(product.id);
  }

  prepareRecommendation() {
    if (this.preparedRecommendation) return Promise.resolve(this.preparedRecommendation);
    if (this.recommendationPreparationPromise) return this.recommendationPreparationPromise;

    this.recommendationPreparationPromise = this.buildPreparedRecommendation().finally(() => {
      this.recommendationPreparationPromise = null;
    });

    return this.recommendationPreparationPromise;
  }

  async buildPreparedRecommendation() {
    const recommendation = this.querySelector('[data-ts-cart-recommendation]');
    if (!recommendation) return;

    try {
      const [products, cartResponse] = await Promise.all([
        this.getTopSellingProducts(),
        fetch('/cart.js', { headers: { Accept: 'application/json' } }),
      ]);

      if (!cartResponse.ok) throw new Error(`Cart request failed with status ${cartResponse.status}`);

      const cart = await cartResponse.json();
      const cartProductIds = new Set((cart.items || []).map((item) => Number(item.product_id)));
      const currentRecommendationId = Number(recommendation.dataset.productId || 0);

      const availableProducts = products.filter(
        (product) => product.available && product.variant_id && product.image && !cartProductIds.has(Number(product.id))
      );

      const freshProducts = availableProducts.filter(
        (product) =>
          Number(product.id) !== currentRecommendationId && Number(product.id) !== Number(this.lastRecommendationId || 0)
      );

      const candidates = freshProducts.length ? freshProducts : availableProducts;
      if (!candidates.length) return;

      const product = candidates[this.getRandomIndex(candidates.length)];
      await this.preloadRecommendationImage(product.image);
      this.preparedRecommendation = product;
      return product;
    } catch (error) {
      console.warn('Cart recommendation could not be prepared:', error);
    }
  }

  moneyFromShopifyCents(value) {
    return Math.round(Number(value) || 0) / 100;
  }

  buildTbiItems(cart) {
    return cart.items.map((item) => {
      let itemName = item.product_title || item.title || 'Product';

      if (item.variant_title && item.variant_title !== 'Default Title') {
        itemName += ` - ${item.variant_title}`;
      }

      return {
        name: itemName,
        price: this.moneyFromShopifyCents(item.final_price),
        qty: item.quantity,
        total: this.moneyFromShopifyCents(item.final_line_price),
        imagelink: item.image || '',
        category: 0,
      };
    });
  }

  closeTbiPopup() {
    document.getElementById('tbiPopup')?.remove();
    document.body.classList.remove('tbi-popup-open');
  }

  openTbiPopup(url) {
    this.closeTbiPopup();

    const popup = document.createElement('div');
    popup.id = 'tbiPopup';
    popup.innerHTML = `
      <div class="tbi-popup-inner" role="dialog" aria-modal="true">
        <button id="customTbiClose" type="button" class="tbi-popup-close" aria-label="Затвори">×</button>
        <iframe src="${url}" title="TBI Bank" loading="lazy"></iframe>
      </div>
    `;

    popup.addEventListener('click', (event) => {
      if (event.target === popup || event.target.classList.contains('tbi-popup-close')) {
        this.closeTbiPopup();
      }
    });

    document.body.appendChild(popup);
    document.body.classList.add('tbi-popup-open');
  }

  async handleTbiClick(button) {
    if (button.dataset.tsTbiPending === '1') return;

    const originalButtonHtml = button.innerHTML;
    button.dataset.tsTbiPending = '1';
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.innerHTML = 'Свързване с TBI…';

    try {
      const response = await fetch('/cart.js', {
        method: 'GET',
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) throw new Error(`Cart request failed with status ${response.status}`);

      const cart = await response.json();
      const cartTotal = this.moneyFromShopifyCents(cart.total_price);

      if (!cart.items?.length) {
        alert('Количката е празна.');
        return;
      }

      if (cartTotal < 50 || cartTotal > 15000) {
        alert('TBI предлага финансиране за суми между 50 EUR и 15000 EUR.');
        return;
      }

      if (typeof window.tsRegisterTbiBasket !== 'function') {
        throw new Error('TBI registration service is not available');
      }

      const data = await window.tsRegisterTbiBasket({
        reseller_code: 'BIWD',
        reseller_key: 'timeshop',
        items: this.buildTbiItems(cart),
      });

      this.openTbiPopup(data.url);
    } catch (error) {
      console.error('Cart drawer TBI error:', error);
      alert('Временен проблем при свързването с TBI. Моля, опитайте отново.');
    } finally {
      delete button.dataset.tsTbiPending;
      button.disabled = false;
      button.removeAttribute('aria-busy');
      button.innerHTML = originalButtonHtml;
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLinks = document.querySelectorAll('#cart-icon-bubble, [data-ts-mobile-cart-trigger]');
    if (!cartLinks.length) return;

    cartLinks.forEach((cartLink) => {
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.setAttribute('aria-controls', 'CartDrawer');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        const focusTarget = this.closeMobileMenuBeforeCart(cartLink) || cartLink;
        this.open(focusTarget);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          const focusTarget = this.closeMobileMenuBeforeCart(cartLink) || cartLink;
          this.open(focusTarget);
        }
      });
    });
  }

  closeMobileMenuBeforeCart(trigger) {
    const drawer = trigger?.closest?.('header-drawer');
    const details = drawer?.querySelector('#Details-menu-drawer-container');
    if (!drawer || !details?.hasAttribute('open')) return null;

    const summary = details.querySelector(':scope > summary');
    details.classList.remove('menu-opening');
    details.querySelectorAll('details').forEach((submenu) => {
      submenu.removeAttribute('open');
      submenu.classList.remove('menu-opening');
    });
    details.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    details.removeAttribute('open');
    summary?.setAttribute('aria-expanded', 'false');

    document.documentElement.classList.remove('ts-drawer-open');
    document.querySelector('.section-header')?.classList.remove('menu-open');
    document.body.classList.remove(
      'overflow-hidden-tablet',
      'overflow-hidden-desktop',
      'overflow-hidden-mobile'
    );
    if (drawer.dataset.breakpoint) {
      document.body.classList.remove(`overflow-hidden-${drawer.dataset.breakpoint}`);
    }
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus();
    }
    if (typeof drawer.onResize === 'function') {
      window.removeEventListener('resize', drawer.onResize);
    }

    return summary;
  }

  open(triggeredBy) {
    clearTimeout(this.closeTimer);

    if (triggeredBy) this.setActiveElement(triggeredBy);

    const preparedRecommendation = this.preparedRecommendation;
    if (preparedRecommendation) {
      if (Number(preparedRecommendation.id) !== Number(this.addedProductId || 0)) {
        this.renderCartRecommendation(preparedRecommendation);
      }

      this.preparedRecommendation = null;
    }

    clearTimeout(this.recommendationPreparationTimer);
    this.recommendationPreparationTimer = setTimeout(() => this.prepareRecommendation(), 650);

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.ts-cart-drawer__surface') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    clearTimeout(this.closeTimer);
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);

    const closeDelay = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 520;
    this.closeTimer = setTimeout(() => {
      if (!this.classList.contains('active')) {
        document.body.classList.remove('overflow-hidden');
      }
    }, closeDelay);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.addedProductId = parsedState.product_id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
